<template>
  <div
    class="universal-search-wrap"
    v-click-outside="closeSearchPopup"
  >
    <div class="dropdown-toggle">
      <select
        name="search_type"
        v-model.trim="searchType"
        class="_h100"
        title="Select search category"
      >
        <option
          v-for="(item, index) in searchTypes"
          :value="index"
          :key="index"
        >
          {{item.title}}
        </option>
      </select>
      <div class="toggles">
        <font-awesome-icon
          icon="caret-down"
          aria-label="Toggle collapsed"
        />
      </div>
    </div>
    <form
      class="searchbar"
      @submit.prevent="search"
    >
      <app-placeholder-input
        @click="searchRefinementVisible = true"
        class="universal-search"
        :label="searchPlaceholder"
        name="universal_search"
        v-model="universalSearch"
      />
      <button
        type="submit"
        class="search"
      >
        Search
      </button>
    </form>
    <transition
      v-on:enter="enter"
      v-on:leave="leave"
    >
      <div
        class="search-refinement"
        v-if="searchRefinementVisible"
      >
        <group-selector v-if="searchType !== 'group'" />
        <div
          class="advanced-search"
          v-if="searchType !== 'invoice'"
        >
          <div class="_bg-background-body _text-center">
            <button
              @click="onAdvanceSearchClick"
              class="white --sm"
            >
              Advanced Search
              <font-awesome-icon
                v-if="advancedSearchVisible"
                icon="caret-up"
                aria-label="Collapse advanced search"
              />
              <font-awesome-icon
                v-else
                icon="caret-down"
                aria-label="Expand advanced search"
              />
            </button>
          </div>
          <transition
            v-on:enter="enter"
            v-on:leave="leave"
          >
            <form
              v-if="advancedSearchVisible"
              @submit.prevent="search"
            >
              <div
                v-if="searchType === 'member'"
                class="advanced-search-form"
              >
                <div class="field-group">
                  <app-placeholder-input
                    label="Member ID"
                    name="client-id"
                    v-model.trim="searchValues.member.memberId"
                  />
                </div>
                <div class="field-group">
                  <app-placeholder-input
                    label="SSN"
                    name="ssn"
                    v-model.trim="searchValues.member.ssn"
                  />
                </div>
                <div class="field-group">
                  <app-placeholder-date-input
                    label="DOB"
                    name="dob"
                    v-model.trim="searchValues.member.dob"
                  ></app-placeholder-date-input>
                </div>
                <div class="field-group">
                  <app-placeholder-select
                    label="Status"
                    name="memberStatus"
                    :options="memberStatusOptions"
                    v-model.trim="searchValues.member.memberStatus"
                  ></app-placeholder-select>
                </div>
              </div>
              <div
                v-if="searchType === 'user'"
                class="advanced-search-form"
              >
                <div class="field-group">
                  <app-placeholder-input
                    label="First Name"
                    name="first-name"
                    v-model.trim="searchValues.user.firstName"
                  />
                </div>
                <div class="field-group">
                  <app-placeholder-input
                    label="Last Name"
                    name="last-name"
                    v-model.trim="searchValues.user.lastName"
                  />
                </div>
                <div class="field-group">
                  <app-placeholder-select
                    label="Status"
                    name="userStatus"
                    :options="userStatusOptions"
                    v-model.trim="searchValues.user.status"
                  ></app-placeholder-select>
                </div>
                <div class="field-group">
                  <app-placeholder-input
                    label="Email"
                    name="email"
                    v-model.trim="searchValues.user.email"
                  />
                </div>
              </div>
              <div
                v-if="searchType === 'group'"
                class="advanced-search-form"
              >
                <div class="field-group -full-width">
                  <app-placeholder-input
                    label="Group ID"
                    name="group-id"
                    v-model.trim="searchValues.group.id"
                  />
                </div>
              </div>
              <div class="field-group -full-width">
                <button
                  type="submit"
                  class="--sm -full-width"
                >Search</button>
              </div>
            </form>
          </transition>
        </div>
      </div>
    </transition>
  </div>
</template>
<script>
// import ContextPicker from '@organisms/ContextPicker';
import GroupSelector from '@organisms/GroupSelector';
import AppPlaceholderDateInput from '@atoms/PlaceholderDateInput';
import AppPlaceholderInput from '@atoms/PlaceholderInput';
import AppPlaceholderSelect from '@atoms/PlaceholderSelect';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import accordionTransition from '@mixins/accordionTransition';
import { eventBus } from '@/main';
import { computed, onBeforeMount, reactive, ref, watch } from '@vue/composition-api';
import { useRoute, useRouter, useStore } from '@vue3shims';
import { useGroupSelector } from '@composables/useGroupSelector';

export default {
  mixins: [accordionTransition],
  setup() {
    const store = useStore();
    const route = useRoute();
    const router = useRouter();
    const { selected } = useGroupSelector();

    const advancedSearchVisible = ref(false);

    const allSearchTypes = {
      member: {
        title: 'Member',
        searchPlaceholder: 'Search Last, First'
      },
      user: {
        title: 'Admin',
        searchPlaceholder: 'Search Administrator User ID'
      },
      invoice: {
        title: 'Invoice',
        searchPlaceholder: 'Search Invoice Number'
      },
      group: {
        title: 'Group',
        searchPlaceholder: 'Search Group Name'
      }
    };

    const closeSearchPopup = () => {
      searchRefinementVisible.value = false;
    }

    const initialSearchValues = () => {
      return {
        member: {
          universalSearchField: 'fullName',
          fullName: '',
          memberStatus: 'all',
          memberId: '',
          ssn: '',
          dob: ''
        },
        user: {
          universalSearchField: 'userId',
          userId: '',
          firstName: '',
          lastName: '',
          email: '',
          status: 'all'
        },
        invoice: {
          universalSearchField: 'invoiceNumber',
          invoiceNumber: '',
          invoiceDate: ''
        },
        group: {
          universalSearchField: 'name',
          name: '',
          id: ''
        }
      }
    }

    const memberStatusOptions = [
      { text: 'All', value: 'all' },
      { text: 'Active', value: 'active' },
      { text: 'Inactive', value: 'inactive' }
    ];

    const minimumAccess = computed(() => store.getters.minimumAccess);

    const onAdvanceSearchClick = () => {
      advancedSearchVisible.value = !advancedSearchVisible.value;
    }

    const resetSearch = () => {
      // searchValues = searchValues();
      advancedSearchVisible.value = false;
    }

    const search = () => {
      // copy search values
      const searchFields = JSON.parse(JSON.stringify(searchParams.value));
            // clear the form
      searchValues.value = initialSearchValues();
      closeSearchPopup();

      if (!searchValues.value[searchType.value]) {
        console.warn(`Invalid universal search type ${searchType.value}`)
        return '';
      }

      if (searchType.value === 'invoice') {
        router.push({ name: 'invoice-details', params: { invoiceNumber: searchFields.invoiceNumber } })
        return
      }
      // console.log(`searchFields`, searchFields);
      let searchRouteName = `search-${searchType.value}`
      // const reload = () => window.location.reload(true) // Not sure why we need to handle reload here?
      // const err = (err) => { if(err.name !== 'NavigationDuplicated') throw err }
      store.dispatch('LOAD_QUERY_PARAMS', searchFields);
      router.push({ name: searchRouteName })
    }

    const searchPlaceholder = computed(() => searchTypes.value[searchType.value] ? searchTypes.value[searchType.value].searchPlaceholder : '');

    const searchRefinementVisible = ref(false);

    const searchType = computed({
      get() {
        return actualSearchType.value;
      },
      set(newSearchType) {
        actualSearchType.value = newSearchType;
      }
    });

    const searchTypes = computed(() => {
      let typesToShow = JSON.parse(JSON.stringify(allSearchTypes));
      if (
        !minimumAccess.value.memberSearchAllowed &&
        !minimumAccess.value.memberMaintenanceAllowed
      ) {
        delete typesToShow['member']
      }
      if (!minimumAccess.value.viewInvoicesAllowed && !minimumAccess.value.claimsAllowed) {
        delete typesToShow['invoice']
      }
      if (!minimumAccess.value.createGroupUsersAllowed) {
        delete typesToShow['user']
      }
      // console.log(`typesToShow`, typesToShow);
      return typesToShow;
    });

    const actualSearchType = ref(Object.keys(searchTypes.value)[0]);

    const searchValues = ref(initialSearchValues());

    const userStatusOptions = [
      { text: 'All', value: 'all' },
      { text: 'Pending', value: 'pending' },
      { text: 'Active', value: 'active' },
      { text: 'Inactive', value: 'inactive' }
    ];

    const searchParams = computed(() => {
      let params = {
        ...searchValues.value[searchType.value]
      }
      if (params.fullName) {
        params = splitFullName(params)
      }

      for (let key in params) {
        params[key] = params[key].trim()
      }
      switch (searchType.value) {
        case 'member':
        case 'user':
        case 'invoice':
          params = {
            ...params,
            ...selected.value,
          }
          break;
        case 'group':
          params = { ...params }
          break;
      }
      delete params.fullName;
      delete params.universalSearchField;
      console.log(`params`, params);
      return params;
    });

    const splitFullName = (searchVal) => {
      let fullNameAsLastFirst = searchVal.fullName;
      let firstName = '';
      let lastName = '';
      fullNameAsLastFirst = fullNameAsLastFirst.trim()
      if (fullNameAsLastFirst.indexOf(',') !== -1) {
        [lastName, firstName] = fullNameAsLastFirst.split(',');
      } else if (fullNameAsLastFirst.indexOf(' ') !== -1) {
        [lastName, firstName] = fullNameAsLastFirst.split(' ');
      } else if (fullNameAsLastFirst.indexOf(', ') !== -1) {
        [lastName, firstName] = fullNameAsLastFirst.split(', ');
      } else {
        lastName = fullNameAsLastFirst;
      }

      lastName = lastName.trim();
      firstName = firstName.trim();

      return {
        ...searchVal,
        ...{ firstName: firstName, lastName: lastName }
      }
    }
    // so this is literally what the user typed in the text box.  No idea
    //    why this was the name they went with.
    const universalSearch = computed({
      get() {
        const universalSearchType = searchValues.value[searchType.value]
        if (!searchValues.value[searchType.value]) {
          console.warn(`Invalid universal search type ${searchType.value}`)
          return ''
        }

        const universalSearchFieldName = searchValues.value[searchType.value].universalSearchField
        if (!universalSearchFieldName) {
          console.warn(`Invalid universal search field name ${universalSearchFieldName}`)
          return ''
        }

        const universalSearchFieldValue = searchValues.value[searchType.value][universalSearchFieldName]
        if (universalSearchFieldValue === undefined) {
          console.warn(`Invalid universal search field ${universalSearchType} ${universalSearchFieldName}`)
          return ''
        }

        return universalSearchFieldValue
      },
      set(newValue) {
        const universalSearchType = searchValues.value[searchType.value]
        if (!searchValues.value[searchType.value]) {
          console.warn(`Invalid universal search type ${searchType.value}`)
          return ''
        }

        const universalSearchFieldName = searchValues.value[searchType.value].universalSearchField
        if (!universalSearchFieldName) {
          console.warn(`Invalid universal search field name ${universalSearchFieldName}`)
          return ''
        }

        if (searchValues.value[searchType.value][universalSearchFieldName] === undefined) {
          console.warn(`Invalid universal search field ${universalSearchType} ${universalSearchFieldName}`)
          return ''
        }
        // console.log(`newValue`, newValue);
        searchValues.value[searchType.value][universalSearchFieldName] = newValue
      }
    });


    onBeforeMount(() => {
      // console.log(`OBM`, gs);
      // TODO:  figure out what this is and why...
      eventBus.$on('searchParamsParsed', resetSearch);
    });


    return {
      actualSearchType,
      advancedSearchVisible,
      closeSearchPopup,
      memberStatusOptions,
      onAdvanceSearchClick,
      resetSearch,
      search,
      searchPlaceholder,
      searchRefinementVisible,
      searchType,
      searchTypes,
      searchValues,
      universalSearch,
      userStatusOptions,
    }

  },
  components: {
    GroupSelector,
    FontAwesomeIcon,
    AppPlaceholderInput,
    AppPlaceholderDateInput,
    AppPlaceholderSelect
  },
}
</script>

<style>
  .advanced-search-form input.form-input, select.form-input {
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1) !important;
  }
</style>
